import { useScreenSize } from '@payfit/growth-platform-brand-system-v2';
import Chevron from '@payfit/growth-platform-brand-system-v2/foundations/pictograms/chevron_right.svg';
import Less from '@payfit/growth-platform-brand-system-v2/foundations/pictograms/less.svg';
import More from '@payfit/growth-platform-brand-system-v2/foundations/pictograms/more.svg';
import { addUnbreakableSpace } from 'apps/website/src/utils/textUtils';
import { classNames } from 'libs/growth-platform-brand-system-v2/src/utils/style';
import React, { useEffect, useRef, useState } from 'react';
import { Submenu } from '.';
import { menuItemCn } from '../styles';

export const SubmenuLink = ({ menuItem, locale, index }: any) => {
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const screenSize = useScreenSize();
  const isMobile = !['xl', 'xxl'].includes(screenSize);
  const submenuRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    setSubmenuOpen(false);
  }, [screenSize]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | KeyboardEvent) => {
      if (
        submenuRef.current &&
        !submenuRef.current.contains(event.target as Node)
      ) {
        setSubmenuOpen(false);
      }
    };

    const removeEventListener = () => {
      document.removeEventListener('mouseup', handleClickOutside);
      document.removeEventListener('keyup', handleClickOutside);
    };

    if (submenuOpen) {
      document.addEventListener('mouseup', handleClickOutside);
      document.addEventListener('keyup', handleClickOutside);
    } else {
      removeEventListener();
    }

    return () => {
      removeEventListener();
    };
  }, [submenuRef, submenuOpen]);

  return (
    <li
      ref={submenuRef}
      data-tracking-submenu={menuItem.title}
      className={classNames(
        'menuItem',
        submenuOpen && 'submenu-open',
        'group',
        'relative',
        'xl:mb-0 xl:mt-0',
        'text-neutral-301',
        'whitespace-nowrap',
      )}
      onMouseEnter={() => {
        if (!isMobile) {
          setSubmenuOpen(true);
        }
      }}
      onMouseLeave={() => {
        if (!isMobile) {
          setSubmenuOpen(false);
        }
      }}
      role="menu"
    >
      <button
        aria-haspopup="true"
        aria-expanded={submenuOpen}
        aria-controls={`submenu-${menuItem.id}`}
        onKeyUp={(e) => {
          if (e.key === 'Escape') {
            setSubmenuOpen(false);
          }
          if (e.key === ' ') {
            setSubmenuOpen(!submenuOpen);
          }
        }}
        onClick={() => {
          setSubmenuOpen(!submenuOpen);
        }}
        className={classNames(
          menuItemCn,
          'xl:submenu-open:bg-silver-40 xl:submenu-open:text-navy-200',
        )}
      >
        {addUnbreakableSpace(menuItem.title)}
        <span className="ml-1 hidden h-full items-center xl:flex">
          {/* Chevron for xl screens */}
          <Chevron
            className={classNames(
              'fill-current w-[20px] transition-all duration-200',
              'rotate-90',
              'transform group-hover:-rotate-90 submenu-open:-rotate-90',
              'fill-[#768699] text-[#768699]',
              '-mr-[5px]',
            )}
          />
        </span>
        <span
          className={classNames('relative h-[14px] w-[14px]', 'ml-1 xl:hidden')}
        >
          <Less
            className={classNames(
              'h-[14px] w-[14px]',
              'transition duration-200',
              'absolute right-0 top-0',
              'opacity-0',
              submenuOpen && 'rotate-180 opacity-100',
            )}
          />
          <More
            className={classNames(
              'h-[14px] w-[14px]',
              'transition duration-200',
              'transform transition-all duration-200 xl:group-hover:rotate-0',
              'fill-[#768699] text-[#768699]',
              'absolute right-0 top-0',
              submenuOpen && 'rotate-180 opacity-0',
            )}
          />
        </span>
      </button>
      <Submenu {...menuItem} index={index} locale={locale} />
    </li>
  );
};
